import { Api } from "./api";
import { Dispatch } from "redux";
import { IApplicationState } from "../redux/reducers";
import config from "../utils/config";
import {
	GetIngredientsAction,
	getStarredIngredientsAction,
	getIngredientDetailAction,
	getIngredientPersonaAction,
	getSimiliarIngredientsAction,
	getIngredientStatusAction,
	getRecentlyViewedAction,
	GetIngredientSearchAction,
	getIngredientUsageAction,
	getIngredientPreviewAction,
	GetPaginatedIngredientSearchAction,
	SetIngredientPagination
} from "../redux/actions/ingredients/ingredientActions";
import notification, { ingredient } from "../utils/notification";
import { info } from "../utils/notification";
import { IIngredient, IIngredientFormData } from "../interfaces/ingredient";

export class IngredientService extends Api {
	addIngredientToPortfolio(id: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const {
				search: { term }
			} = getState();

			try {
				await this.http.post(config.api.endpoints.ingredients.addToPortfolio, {
					ingredients: [{ id }]
				});
				// fetch ingredients again
				await this.getIngredients(term)(dispatch, getState);
				notification.ingredient("Ingredient added to portfolio");
			} catch (error) {
				this.handleError(error, "Add Ingredient to Portfolio");
			}
		};
	}

	addIngredient(ingredientData: any) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const {
				search: { term }
			} = getState();

			try {
				await this.http.post(config.api.endpoints.ingredients.addToPortfolio, {
					...ingredientData
				});
				// fetch ingredients again
				await this.getIngredients(term)(dispatch, getState);
				notification.ingredient("Ingredient added sucessfully");
			} catch (error) {
				this.handleError(error, "Add Ingredient");
			}
		};
	}

	getIngredients(searchTerm?: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const {
				ingredient: {
					ingredients: { pagination }
				},
				user: { role }
			} = getState();

			try {
				const endpoint = searchTerm
					? config.api.endpoints.ingredients.searchPortfolio(role, searchTerm)
					: config.api.endpoints.ingredients.all(role);

				const response = searchTerm
					? await this.http.get(endpoint)
					: await this.http.get(endpoint, {
						params: {
							offset: pagination.page,
							limit: pagination.size,
							similar_ingredients: role === "JF_ADMIN"
						}
					});

				/**
				 * Data coming from a portfolio search is structured differently than w/out a search term provided;
				 * Response action parameter differences reflect this
				 */

				if (searchTerm) {
					if (response.data.length == 0) {
						dispatch(GetIngredientSearchAction([]));
						return;
					}
					dispatch(GetIngredientSearchAction(response.data));
				} else {
					dispatch(GetIngredientSearchAction([]));
					if (response.data.length == 0 || response.data.data.length == 0) {
						dispatch(
							GetIngredientsAction({
								list: [],
								pagination: {
									page: 1,
									pages: 1,
									size: 20,
									total: 0
								}
							})
						);
						return;
					}
					dispatch(
						GetIngredientsAction({
							list: response.data.data,
							pagination: response.data.meta
						})
					);
				}
			} catch (error) {
				this.handleError(error, `Ingredient ${searchTerm ? "Search" : ""} Error`);
			}
		};
	}

	getStarredIngredients() {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			try {
				const response = await this.http.get(config.api.endpoints.ingredients.starred);
				const ingredients = Array.isArray(response.data) ? response.data : [];
				dispatch(getStarredIngredientsAction(ingredients));
			} catch (error) {
				this.handleError(error, "Starred Ingredients");
			}
		};
	}

	getIngredientFilterMenu(page: any, term: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			try {
				const response = await this.http.get(config.api.endpoints.ingredients.searchMenu(term));
				

				const ingredients = Array.isArray(response.data) ? response.data : [];

				// dispatch(getStarredIngredientsAction(ingredients));
			} catch (error) {
				this.handleError(error, "Starred Ingredients");
			}
		};
	}

	starIngredient(id: string, isStarred: boolean) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const {
					ingredient: { starred }
				} = getState();

				let starredIngredients = [];

				if (!isStarred) {
					starredIngredients = [...starred, { id }].map((item) => ({
						id: item.id
					}));
				} else {
					starredIngredients = (starred as any)
						.map((item: any) => ({
							id: item.id
						}))
						.filter((item: any) => item.id !== id);
				}

				await this.http.post(config.api.endpoints.ingredients.starred, {
					ingredients: starredIngredients
				});

				const response = await this.http.get(config.api.endpoints.ingredients.starred);
				if (Array.isArray(response.data)) {
					dispatch(getStarredIngredientsAction(response.data));
					return;
				}
				dispatch(getStarredIngredientsAction([]));
			} catch (error) {
				this.handleError(error, "Star ingredient");
			}
		};
	}

	getIngredientById(id: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			try {
				dispatch(getIngredientDetailAction(null));
				const response = await this.http.get(
					config.api.endpoints.ingredients.id(id, getState().user.role)
				);

				dispatch(getIngredientDetailAction(response.data));

				return response.data;
			} catch (error) {
				notification.ingredient("Ingredient doesn't exist");
			}
		};
	}

	getIngredientStatus(ingredientId: string) {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(config.api.endpoints.ingredients.status(ingredientId));
				const { status } = response.data;
				dispatch(getIngredientStatusAction(status));
			} catch (error) {
				this.handleError(error, "Get Ingredient Status");
			}
		};
	}

	getIngredientPersona(ingredientId: string) {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(
					config.api.endpoints.ingredients.ingredientPersonas(ingredientId)
				);
				dispatch(getIngredientPersonaAction(response.data.top_personas));
			} catch (error) {
				// Silently handle error for non-critical data - persona data is optional
				// Dispatch empty object to prevent UI errors
				dispatch(getIngredientPersonaAction({}));
			}
		};
	}

	getSimliarIngredientsById(ingredientId: string) {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(
					config.api.endpoints.ingredients.similiarIngredients(ingredientId)
				);
				dispatch(getSimiliarIngredientsAction(response.data.similar_ingredients));
			} catch (error) {
				// Silently handle error for non-critical data - similar ingredients are optional
				// Dispatch empty array to prevent UI errors
				dispatch(getSimiliarIngredientsAction([]));
			}
		};
	}

	getRecommendedIngredientsById(ingredientId: string) {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(
					config.api.endpoints.ingredients.recommendedIngredients(ingredientId)
				);
				return response.data;
			} catch (error) {
				// Return empty data structure for non-critical recommendations
				// This prevents calling code from breaking while not showing error to user
				return { recommendations: [] };
			}
		};
	}

	getCompareIngredients(firstIngredientId: string, secondIngredientId: string) {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(
					config.api.endpoints.ingredients.compareIngredients(firstIngredientId, secondIngredientId)
				);
				return response.data;
			} catch (error) {
				this.handleError(error, "Recommendation");
			}
		};
	}

	searchIngredient(name: string, text?: string, page?: number) {
		return async (dispatch: Dispatch, getState?: () => IApplicationState) => {
			try {
				if (text) {
					if (!getState) throw new Error("getState is required when no text is provided.");

					const {
						ingredient: {
							ingredients: { pagination }
						}
					} = getState();

					const response = await this.http.get(config.api.endpoints.ingredients.search(name), {
						params: {
							offset: page ? page : pagination.page,
							limit: pagination.size
						}
					});

					const ingredients = Array.isArray(response.data) ? response.data : [];
					dispatch(GetPaginatedIngredientSearchAction(ingredients));
					return response.data;
				} else {
					const response = await this.http.get(config.api.endpoints.ingredients.search(name));
					return response.data;
				}
			} catch (error) {
				this.handleError(error, "Ingredient Search");
				return [];
			}
		};
	}

	removeIngredient(ingredientId: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const {
				search: { term }
			} = getState();

			try {
				const response = await this.http.delete(
					config.api.endpoints.ingredients.removeIngredient(ingredientId)
				);
				await this.getIngredients(term)(dispatch, getState);
				info("Ingredient successfully removed.");
			} catch (error) {
				this.handleError(error, "Remove Ingredient");
			}
		};
	}

	addRateRecommendation(recommendationId: string, rate: any) {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.patch(
					config.api.endpoints.ingredients.rateRecommendation(recommendationId),
					{
						rating: rate.starNumber,
						rating_cost: rate.costNumber,
						rating_nutrition: rate.nutritionNumber,
						rating_sustainability: rate.sustainabilityNumber
					}
				);
				return response.data;
			} catch (error) {
				this.handleError(error, "Rating");
			}
		};
	}

	addRecentlyViewed(id: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			try {
				const {
					ingredient: { recentlyViewed }
				} = getState();
				if (!recentlyViewed.find((i: any) => i.id === id)) {
					const response = await this.http.post(
						config.api.endpoints.ingredients.addRecentlyViewed(id)
					);
				}
			} catch (error) {
				this.handleError(error, "Recently Viewed");
			}
		};
	}

	getRecentlyViewed() {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(config.api.endpoints.ingredients.recentlyViewed);
				dispatch(getRecentlyViewedAction(response.data));
				return response.data;
			} catch (error) {
				return [];
			}
		};
	}

	editIngredient(ingredient_id: string, ingredientData: IIngredientFormData) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			try {
				const {
					search: { term }
				} = getState();
				const response = await this.http.put(
					config.api.endpoints.ingredients.edit(ingredient_id),
					ingredientData
				);
				dispatch(getIngredientDetailAction(response.data));
				ingredient("Ingredient updated successfully");
				await this.getIngredients(term)(dispatch, getState);
			} catch (error) {
				this.handleError(error, "Edit Ingredient");
			}
		};
	}

	uploadIngredientInfoFile(ingredient_id: string, file: File) {
		return async () => {
			try {
				const formData = new FormData();
				formData.append("ingredient_file", file);
				const response = await this.http.post(
					config.api.endpoints.ingredients.infoFileUpload(ingredient_id),
					formData
				);
				return response.data;
			} catch (error) {
				return error;
			}
		};
	}

	getIngredientUsage() {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(config.api.endpoints.analytics.ingredientUsage);
				dispatch(getIngredientUsageAction(response.data));
				return response.data;
			} catch (error) {
				return [];
			}
		};
	}

	getIngredientBuyersPreview() {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(config.api.endpoints.products.buyersPreview);
				dispatch(getIngredientPreviewAction(response.data));
				return response.data;
			} catch (error) {
				return [];
			}
		};
	}

	//version 1
	// getPaginatedIngredients(offset = 0, limit = 20) {
	//     return async (dispatch: Dispatch, getState: () => IApplicationState) => {
	//         try {
	//             const response = await this.http.get(config.api.endpoints.admin.adminIngredients, {
	//                 params: {
	//                     offset,
	//                     limit,
	//                     similar_ingredients: true,
	//                 }
	//             });

	//             // Dispatch an action if needed here, e.g. to store ingredients and pagination
	//             dispatch(GetIngredientsAction({
	//                 list: response.data.data || [],
	//                 pagination: response.data.meta
	//             }));

	//             return {
	//                 list: response.data.data || [],
	//                 total: response.data.meta?.total || 0,
	//             };
	//         } catch (error) {
	//             this.handleError(error, "Fetch Paginated Ingredients");
	//             return { list: [], total: 0 };
	//         }
	//     }
	// }

	//version 2
	// getPaginatedIngredients(all: boolean = false, companyId?: string) {
	//   return async (dispatch: Dispatch, getState: () => IApplicationState) => {
	//     try {
	//       const {
	//         ingredient: {
	//           ingredients: { pagination }
	//         }
	//       } = getState();

	//       let params: Record<string, any> = {};
	//       if (!all) {
	//         params.offset = pagination.page;
	//         params.limit = pagination.size;
	//       }
	//       if (companyId) {
	//         params.company_id = companyId;
	//       }
	//       // Add similar_ingredients only if backend supports it (or set based on role etc)
	//       // params.similar_ingredients = true;

	//       const response = await this.http.get(config.api.endpoints.admin.adminIngredients, { params });

	//       dispatch(GetIngredientsAction({
	//         list: all ? response.data : response.data.data,
	//         pagination: response.data.meta
	//       }));

	//       return {
	//         list: all ? response.data : response.data.data,
	//         pagination: response.data.meta
	//       };
	//     } catch (error) {
	//       this.handleError(error, "Fetch Paginated Ingredients");
	//       return { list: [], pagination: { total: 0, pages: 0, page: 0, size: 0 } };
	//     }
	//   };
	// }

	//v-3
	//   getPaginatedIngredients(all: boolean = false, companyId?: string) {
	//   return async (dispatch: Dispatch, getState: () => IApplicationState) => {
	//     try {
	//       const {
	//         ingredient: {
	//           ingredients: { pagination }
	//         }
	//       } = getState();

	//       let params: Record<string, any> = {};
	//       if (!all) {
	//         // Convert 1-based page number to zero-based offset for API
	//         params.offset = ((pagination.page || 1) - 1) * (pagination.size || 20);
	//         params.limit = pagination.size || 20;
	//       }
	//       if (companyId) {
	//         params.company_id = companyId;
	//       }

	//       // Remove 'similar_ingredients' if unsupported

	//       const response = await this.http.get("/admin/ingredients/", { params });

	//       console.log("API Response in ingredientService:", response.data);

	//       dispatch(GetIngredientsAction({
	//         list: all ? response.data : response.data.data,
	//         pagination: response.data.meta
	//       }));

	//       return {
	//         list: all ? response.data : response.data.data,
	//         pagination: response.data.meta
	//       };
	//     } catch (error) {
	//       console.error("Error fetching paginated ingredients:", error);
	//       return { list: [], pagination: { total: 0, pages: 0, page: 1, size: 20 } };
	//     }
	//   };

	// ingredientService.ts (drop-in replacement for v3)
	getPaginatedIngredients(all: boolean = false, companyId?: string, searchTerm?: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			try {
				const {
					ingredient: {
						ingredients: { pagination }
					}
				} = getState();

				// API expects page number (1-based) in offset parameter, not row offset
				// This matches the pattern used in other services (productService, adminService, etc.)
				const page = Math.max(1, pagination.page || 1);
				const size = Math.max(1, pagination.size || 20);

				const params: Record<string, any> = {};
				if (!all) {
					// Send page number as offset (API expects 1-based page number)
					// Page 1 → offset 1
					// Page 2 → offset 2
					// Page 3 → offset 3
					params.offset = page;
					params.limit = size;
				}
				if (companyId) params.company_id = companyId;
				// Add search parameter if provided
				if (searchTerm && searchTerm.trim()) {
					params.search = searchTerm.trim();
				}

				// Prefer using your config endpoint to avoid path drift
				const endpoint = config.api.endpoints.admin.adminIngredients;

				const response = await this.http.get(endpoint, { params });
				const data = response?.data ?? {};

				// unwrap list safely
				const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);

				// normalize meta into { page, pages, size, total } with 1-based page
				const rawMeta = data?.meta ?? {};
				const meta = {
					page: (() => {
						// Prefer API's page number if provided
						if (rawMeta.page !== undefined && rawMeta.page > 0) {
							return Math.max(1, rawMeta.page);
						}
						// If API echoes back offset, it should be the page number we sent (1-based)
						// Only use it if it matches what we sent (within reasonable range)
						// This prevents using stale/corrupted offset values
						if (rawMeta.offset !== undefined && rawMeta.offset > 0) {
							// If offset matches what we sent (or is close), use it
							// Otherwise, it might be a stale value - ignore it
							const sentOffset = page;
							if (rawMeta.offset === sentOffset || Math.abs(rawMeta.offset - sentOffset) <= 10) {
								return Math.max(1, rawMeta.offset);
							}
							// Offset doesn't match - likely stale, ignore it
						}
						// Fallback: use current page from state (preserve state if API doesn't specify)
						return Math.max(1, pagination.page || 1);
					})(),
					size: rawMeta.size ?? rawMeta.limit ?? rawMeta.page_size ?? size,
					pages:
						rawMeta.pages ??
						rawMeta.total_pages ??
						(rawMeta.total && (rawMeta.size || rawMeta.limit || rawMeta.page_size)
							? Math.max(1, Math.ceil(rawMeta.total / (rawMeta.size || rawMeta.limit || rawMeta.page_size)))
							: (pagination.pages || 1)),
					total: rawMeta.total ?? rawMeta.count ?? 0
				};

				dispatch(
					GetIngredientsAction({
						list,
						pagination: meta
					})
				);

				return { list, pagination: meta };
			} catch (error) {
				console.error("Error fetching paginated ingredients:", error);
				return { list: [], pagination: { total: 0, pages: 1, page: 1, size: 20 } };
			}
		};
	}

	/**
	 * Search ingredients using /ingredients/<search_text>/search/ endpoint
	 * This is specifically for supplier dashboard search functionality
	 */
	searchIngredientsForSupplier(searchText: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			try {
				if (!searchText || !searchText.trim()) {
					// If no search text, fall back to regular paginated ingredients
					return dispatch(this.getPaginatedIngredients(false, undefined, undefined) as any);
				}

				const {
					ingredient: {
						ingredients: { pagination }
					}
				} = getState();

				const page = Math.max(1, pagination.page || 1);
				const size = Math.max(1, pagination.size || 20);

				// Use the search endpoint: /ingredients/<search_text>/search/
				const endpoint = config.api.endpoints.ingredients.search(searchText.trim());

				const response = await this.http.get(endpoint, {
					params: {
						offset: page,
						limit: size
					}
				});

				// Handle different response structures
				const responseData = response?.data ?? {};
				
				// Check if response has data and meta structure
				let list: IIngredient[] = [];
				let rawMeta: any = {};

				if (responseData.data && Array.isArray(responseData.data)) {
					// Response has { data: [], meta: {} } structure
					list = responseData.data;
					rawMeta = responseData.meta ?? {};
				} else if (Array.isArray(responseData)) {
					// Response is directly an array
					list = responseData;
					rawMeta = {};
				} else if (responseData.results && Array.isArray(responseData.results)) {
					// Response has { results: [], count: number } structure
					list = responseData.results;
					rawMeta = {
						total: responseData.count ?? 0,
						page: page,
						size: size
					};
				}

				// Normalize meta into { page, pages, size, total } with 1-based page
				const meta = {
					page: rawMeta.page ?? page,
					size: rawMeta.size ?? rawMeta.limit ?? rawMeta.page_size ?? size,
					pages:
						rawMeta.pages ??
						rawMeta.total_pages ??
						(rawMeta.total && (rawMeta.size || rawMeta.limit || rawMeta.page_size)
							? Math.max(1, Math.ceil(rawMeta.total / (rawMeta.size || rawMeta.limit || rawMeta.page_size)))
							: (list.length > 0 ? Math.max(1, Math.ceil(list.length / size)) : 1)),
					total: rawMeta.total ?? rawMeta.count ?? list.length
				};

				dispatch(
					GetIngredientsAction({
						list,
						pagination: meta
					})
				);

				return { list, pagination: meta };
			} catch (error) {
				console.error("Error searching ingredients:", error);
				this.handleError(error, "Ingredient Search");
				return { list: [], pagination: { total: 0, pages: 1, page: 1, size: 20 } };
			}
		};
	}

	setPaginationSize(size: number, searchTerm?: string) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const state = getState();
			const currentPagination = state.ingredient.ingredients.pagination;

			// Update pagination with new size and reset to page 1
			dispatch(SetIngredientPagination({
				...currentPagination,
				size,
				page: 1
			}));

			// Use search endpoint if search term exists, otherwise use regular paginated ingredients
			if (searchTerm && searchTerm.trim()) {
				await dispatch(this.searchIngredientsForSupplier(searchTerm) as any);
			} else {
				await dispatch(this.getPaginatedIngredients(false, undefined, undefined) as any);
			}
		};
	}
}

export default new IngredientService();
